import React, { useState, useRef, useEffect, useMemo } from 'react';
import { getSidebarRoutes, isRouteActive } from '../config/routeConfig';
import { useStudentProfileStore } from '../stateManagers/stores/studentProfileStore';

interface SidebarProps {
  currentPath: string;
  onNavigate: (routeId: string, params?: Record<string, string>, data?: Record<string, any>) => void;
  onLogout: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ currentPath, onNavigate, onLogout }) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set(['schoolSetup']));
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [submenuHeights, setSubmenuHeights] = useState<Record<string, number>>({});
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [clickedItem, setClickedItem] = useState<string | null>(null);
  const submenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const sidebarRoutes = useMemo(() => getSidebarRoutes(), []);

  // ── Profile from store (same pattern as Dashboard) ────────────────────────
  const { profile, isLoading: profileLoading } = useStudentProfileStore();
  const instituteName = profile?.institute?.name ?? null;
  const branchName    = profile?.branch?.name ?? null;

  useEffect(() => {
    const heights: Record<string, number> = {};
    Object.entries(submenuRefs.current).forEach(([id, ref]) => {
      if (ref) {
        heights[id] = ref.scrollHeight;
      }
    });
    setSubmenuHeights(heights);
  }, []);

  useEffect(() => {
    const newExpanded = new Set<string>();
    sidebarRoutes.forEach(({ id, children }) => {
      if (children && isRouteActive(id, currentPath)) {
        newExpanded.add(id);
      }
    });
    const currentExpandedArray = Array.from(expandedItems).sort();
    const newExpandedArray = Array.from(newExpanded).sort();
    const hasChanges =
      currentExpandedArray.length !== newExpandedArray.length ||
      currentExpandedArray.some((item, index) => item !== newExpandedArray[index]);
    if (hasChanges) {
      setExpandedItems(newExpanded);
    }
  }, [currentPath]);

  useEffect(() => {
    if (isCollapsed) {
      setExpandedItems(new Set());
    }
  }, [isCollapsed]);

  const toggleExpanded = (routeId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(routeId)) {
      newExpanded.delete(routeId);
    } else {
      newExpanded.add(routeId);
    }
    setExpandedItems(newExpanded);
  };

  const handleMouseEnter = (label: string, event: React.MouseEvent<HTMLButtonElement>) => {
    if (isCollapsed) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 10,
      });
      setHoveredItem(label);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const handleNavigate = (routeId: string) => {
    setClickedItem(routeId);
    setTimeout(() => setClickedItem(null), 300);
    setTimeout(() => {
      onNavigate(routeId, {}, {});
    }, 150);
  };

  return (
    <>
      <div
        style={{
          width: isCollapsed ? '80px' : '235px',
          background: 'linear-gradient(180deg, #0e225d 0%, #1a3370 50%, #0a1a3d 100%)',
          padding: isCollapsed ? '18px 9px' : '18px 14px',
          color: 'white',
          minHeight: '100vh',
          boxShadow: '2px 0 9px rgba(14, 34, 93, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 100,
        }}
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div
          style={{
            marginBottom: '23px',
            padding: '7px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            gap: '9px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: '41px',
              height: '41px',
              background: '#fbb21f',
              borderRadius: '9px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: '1.7px solid rgba(255, 255, 255, 0.15)',
            }}
          >
            <i className="pi pi-th-large" style={{ fontSize: '1.25rem', color: '#0e225d' }}></i>
          </div>

          {!isCollapsed && (
            <div style={{ overflow: 'hidden' }}>
              {profileLoading ? (
                <div
                  style={{
                    width: '90px',
                    height: '14px',
                    background: 'rgba(255,255,255,0.12)',
                    borderRadius: '4px',
                    marginBottom: '4px',
                  }}
                />
              ) : (
                <h2
                  style={{
                    margin: 0,
                    fontSize: '1rem',
                    fontWeight: '700',
                    whiteSpace: 'nowrap',
                    letterSpacing: '-0.3px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '160px',
                  }}
                >
                  {instituteName ?? 'HCI'}
                </h2>
              )}

              {!profileLoading && branchName && (
                <p
                  style={{
                    margin: 0,
                    fontSize: '0.68rem',
                    fontWeight: '500',
                    color: 'rgba(251,178,31,0.8)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '160px',
                    letterSpacing: '0.2px',
                  }}
                >
                  {branchName}
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── Toggle Button ────────────────────────────────────────────────── */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            position: 'absolute',
            top: '17px',
            right: '-13px',
            width: '27px',
            height: '27px',
            borderRadius: '7px',
            background: '#0e225d',
            border: '1.7px solid #fbb21f',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 1.7px 7px rgba(14, 34, 93, 0.25)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.12) rotate(180deg)';
            e.currentTarget.style.background = '#1a3370';
            e.currentTarget.style.boxShadow = '0 3.5px 9px rgba(251, 178, 31, 0.35)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
            e.currentTarget.style.background = '#0e225d';
            e.currentTarget.style.boxShadow = '0 1.7px 7px rgba(14, 34, 93, 0.25)';
          }}
        >
          <i
            className={`pi ${isCollapsed ? 'pi-chevron-right' : 'pi-chevron-left'}`}
            style={{ fontSize: '0.72rem', color: '#fbb21f' }}
          ></i>
        </button>

        {/* ── Nav ─────────────────────────────────────────────────────────── */}
        <nav style={{ flex: 1, overflowY: 'auto', paddingRight: '7px' }}>
          {sidebarRoutes.map(({ id, route, children }) => {
            const isActive = isRouteActive(id, currentPath);
            const isExpanded = expandedItems.has(id);
            const hasChildren = children && children.length > 0;
            const isClicked = clickedItem === id;

            return (
              <div key={id} style={{ marginBottom: '4px' }}>
                {/* Parent Menu Item */}
                <button
                  onClick={() => {
                    if (hasChildren && !isCollapsed) {
                      toggleExpanded(id);
                    } else if (hasChildren && isCollapsed) {
                      setIsCollapsed(false);
                      setExpandedItems(new Set([id]));
                    } else {
                      handleNavigate(id);
                    }
                  }}
                  onMouseEnter={(e) => {
                    handleMouseEnter(route.label, e);
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(251, 178, 31, 0.13)';
                      e.currentTarget.style.transform = 'translateX(3px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    handleMouseLeave();
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: isCollapsed ? '11px 7px' : '11px 13px',
                    background: isActive && !hasChildren ? '#fbb21f' : 'transparent',
                    border: 'none',
                    borderRadius: '9px',
                    color: isActive && !hasChildren ? '#0e225d' : 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '0.88rem',
                    fontWeight: '500',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isCollapsed
                      ? 'center'
                      : hasChildren
                      ? 'space-between'
                      : 'flex-start',
                    transform: isClicked ? 'scale(0.95)' : 'scale(1)',
                    boxShadow:
                      isActive && !hasChildren
                        ? '0 2.3px 7px rgba(251, 178, 31, 0.25)'
                        : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i
                      className={`pi ${route.icon}`}
                      style={{
                        marginRight: isCollapsed ? '0' : '11px',
                        fontSize: '1.1rem',
                        transform: isActive && !hasChildren ? 'scale(1.06)' : 'scale(1)',
                      }}
                    ></i>
                    {!isCollapsed && (
                      <span style={{ fontSize: '0.88rem' }}>{route.label}</span>
                    )}
                  </div>
                  {hasChildren && !isCollapsed && (
                    <i
                      className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`}
                      style={{
                        fontSize: '0.78rem',
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      }}
                    ></i>
                  )}
                </button>

                {/* Children (Submenu) */}
                {hasChildren && !isCollapsed && (
                  <div
                    ref={(el) => {
                      submenuRefs.current[id] = el;
                    }}
                    style={{
                      marginTop: '4px',
                      marginLeft: '14px',
                      borderLeft: '1.7px solid rgba(251, 178, 31, 0.2)',
                      paddingLeft: '7px',
                      maxHeight: isExpanded ? `${submenuHeights[id] || 1000}px` : '0px',
                      opacity: isExpanded ? 1 : 0,
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {children.map(({ id: childId, route: childRoute }) => {
                      const isChildActive = isRouteActive(childId, currentPath);
                      const isChildClicked = clickedItem === childId;

                      return (
                        <button
                          key={childId}
                          onClick={() => handleNavigate(childId)}
                          style={{
                            width: '100%',
                            padding: '9px 13px',
                            marginBottom: '3px',
                            background: isChildActive
                              ? 'rgba(251, 178, 31, 0.17)'
                              : 'transparent',
                            border: isChildActive
                              ? '1px solid rgba(251, 178, 31, 0.25)'
                              : '1px solid transparent',
                            borderRadius: '7px',
                            color: 'white',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '0.83rem',
                            fontWeight: '500',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            display: 'flex',
                            alignItems: 'center',
                            opacity: isChildActive ? 1 : 0.85,
                            transform: isChildClicked ? 'scale(0.95)' : 'scale(1)',
                          }}
                          onMouseEnter={(e) => {
                            if (!isChildActive) {
                              e.currentTarget.style.background = 'rgba(251, 178, 31, 0.14)';
                              e.currentTarget.style.opacity = '1';
                              e.currentTarget.style.transform = 'translateX(3px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isChildActive) {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.opacity = '0.85';
                              e.currentTarget.style.transform = 'translateX(0)';
                            }
                          }}
                        >
                          {childRoute.icon && (
                            <i
                              className={`pi ${childRoute.icon}`}
                              style={{ marginRight: '9px', fontSize: '0.88rem' }}
                            ></i>
                          )}
                          <span>{childRoute.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* ── Footer / Logout ──────────────────────────────────────────────── */}
        <div style={{ marginTop: 'auto', paddingTop: '16px' }}>
          {/* Branch pill — visible when expanded and profile loaded */}
          {!isCollapsed && !profileLoading && branchName && (
            <div
              style={{
                padding: '8px 11px',
                marginBottom: '8px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(251,178,31,0.15)',
                borderRadius: '9px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  flexShrink: 0,
                  background: 'rgba(251,178,31,0.15)',
                  borderRadius: '7px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-building" style={{ fontSize: '0.78rem', color: '#fbb21f' }}></i>
              </div>
              <div style={{ overflow: 'hidden' }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: '0.72rem',
                    fontWeight: '600',
                    color: 'rgba(255,255,255,0.9)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '140px',
                  }}
                >
                  {branchName}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: '0.64rem',
                    color: 'rgba(255,255,255,0.4)',
                    letterSpacing: '0.3px',
                  }}
                >
                  Active branch
                </p>
              </div>
            </div>
          )}

          <button
            onClick={onLogout}
            onMouseEnter={(e) => {
              handleMouseEnter('Logout', e);
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.14)';
              e.currentTarget.style.transform = 'translateX(3px)';
            }}
            onMouseLeave={(e) => {
              handleMouseLeave();
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
            style={{
              width: '100%',
              padding: isCollapsed ? '11px 7px' : '11px 13px',
              background: 'transparent',
              border: 'none',
              borderRadius: '9px',
              color: 'white',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: '0.88rem',
              fontWeight: '500',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
            }}
          >
            <i
              className="pi pi-sign-out"
              style={{ marginRight: isCollapsed ? '0' : '9px', fontSize: '1rem' }}
            ></i>
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* ── Tooltip Overlay ──────────────────────────────────────────────────── */}
      {isCollapsed && hoveredItem && (
        <div
          style={{
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateY(-50%)',
            background: '#0e225d',
            color: 'white',
            padding: '7px 13px',
            borderRadius: '7px',
            fontSize: '0.83rem',
            fontWeight: '500',
            boxShadow: '0 2.3px 7px rgba(14, 34, 93, 0.25)',
            zIndex: 9999,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            border: '1px solid #fbb21f',
          }}
        >
          {hoveredItem}
          <div
            style={{
              position: 'absolute',
              left: '-5px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              borderTop: '5px solid transparent',
              borderBottom: '5px solid transparent',
              borderRight: '5px solid #0e225d',
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes tooltipFadeIn {
          from { opacity: 0; transform: translateY(-50%) translateX(-9px); }
          to   { opacity: 1; transform: translateY(-50%) translateX(0); }
        }
        @keyframes slideInSubmenu {
          from { opacity: 0; transform: translateX(-9px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        nav::-webkit-scrollbar { width: 5px; }
        nav::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.05);
          border-radius: 7px;
          margin: 7px 0;
        }
        nav::-webkit-scrollbar-thumb {
          background: rgba(251,178,31,0.3);
          border-radius: 7px;
        }
        nav::-webkit-scrollbar-thumb:hover {
          background: rgba(251,178,31,0.5);
        }
      `}</style>
    </>
  );
};

export default Sidebar;